const RADIUS = 42;
const PROGRESS_PATH_LENGTH = 100;

function segmentPath(offset, percentage) {
  if (percentage === PROGRESS_PATH_LENGTH) {
    return `M 92 50 A ${RADIUS} ${RADIUS} 0 1 1 8 50 A ${RADIUS} ${RADIUS} 0 1 1 92 50`;
  }
  const startAngle = offset / PROGRESS_PATH_LENGTH * Math.PI * 2;
  const endAngle = (offset + percentage) / PROGRESS_PATH_LENGTH * Math.PI * 2;
  return `M ${50 + RADIUS * Math.cos(startAngle)} ${50 + RADIUS * Math.sin(startAngle)} A ${RADIUS} ${RADIUS} 0 ${percentage > 50 ? 1 : 0} 1 ${50 + RADIUS * Math.cos(endAngle)} ${50 + RADIUS * Math.sin(endAngle)}`;
}

export default function CircularProgress({
  value,
  max = 100,
  displayValue,
  label,
  ariaLabel,
  tone = "brand",
  size = "md",
  className = "",
  segments,
  onSegmentClick,
  showSegmentPercentages = false,
}) {
  const numericValue = Number(value);
  const numericMax = Number(max);
  const hasValue = value !== null
    && value !== undefined
    && value !== ""
    && Number.isFinite(numericValue)
    && Number.isFinite(numericMax)
    && numericMax > 0;
  const progress = hasValue ? Math.min(Math.max(numericValue / numericMax, 0), 1) : 0;
  const dashOffset = PROGRESS_PATH_LENGTH * (1 - progress);
  const visibleValue = displayValue ?? (hasValue ? `${Math.round(numericValue)}%` : "-");
  const accessibleLabel = ariaLabel || `${label || "진행률"} ${visibleValue}`;
  const segmentTotal = Array.isArray(segments) && segments.every((segment) =>
    typeof segment?.value === "number" && Number.isFinite(segment.value) && segment.value >= 0)
    ? segments.reduce((sum, segment) => sum + segment.value, 0) : 0;
  const firstSegment = Number.isFinite(segmentTotal) && segmentTotal > 0
    ? segments.find((segment) => segment.value > 0) : null;
  let segmentOffset = 0;
  const segmentRanges = firstSegment ? segments.filter((segment) => segment.value > 0).map((segment) => {
    const percentage = segment.value / segmentTotal * PROGRESS_PATH_LENGTH;
    const offset = segmentOffset;
    segmentOffset += percentage;
    return { segment, percentage, offset };
  }) : [];

  return (
    <div
      className={`ds-circular-progress ds-circular-progress--${size} ds-circular-progress--${tone}${onSegmentClick ? " ds-circular-progress--interactive" : ""}${className ? ` ${className}` : ""}`}
      role={onSegmentClick ? "group" : "img"}
      aria-label={accessibleLabel}
    >
      <svg viewBox="0 0 100 100" aria-hidden={onSegmentClick ? undefined : "true"} role={onSegmentClick ? "group" : undefined}>
        <circle cx="50" cy="50" r={RADIUS} pathLength={PROGRESS_PATH_LENGTH}
          className={`ds-circular-progress__track${firstSegment ? ` ds-circular-progress__track--segmented ds-circular-progress--${firstSegment.tone || tone}` : ""}`}
          style={firstSegment?.color ? { "--ds-circular-color": firstSegment.color } : undefined} />
        {segments === undefined ? <circle
          cx="50"
          cy="50"
          r={RADIUS}
          pathLength={PROGRESS_PATH_LENGTH}
          className="ds-circular-progress__indicator"
          strokeDasharray={`${PROGRESS_PATH_LENGTH} ${PROGRESS_PATH_LENGTH}`}
          strokeDashoffset={dashOffset}
        /> : firstSegment ? segmentRanges.map(({ segment, percentage, offset }, index) => {
          if (onSegmentClick) {
            return (
              <path
                key={segment.key ?? index}
                d={segmentPath(offset, percentage)}
                className={`ds-circular-progress__segment ds-circular-progress--${segment.tone || tone}`}
                style={segment.color ? { "--ds-circular-color": segment.color } : undefined}
                role="button"
                tabIndex={0}
                aria-label={segment.label || String(segment.value)}
                onClick={() => onSegmentClick(segment)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSegmentClick(segment);
                  }
                }}
              >
                {segment.label ? <title>{segment.label}</title> : null}
              </path>
            );
          }
          // Rotate each dash so small segments never cross the closed-circle seam.
          return (
            <circle key={segment.key ?? index} cx="50" cy="50" r={RADIUS} pathLength={PROGRESS_PATH_LENGTH}
              className={`ds-circular-progress__segment ds-circular-progress--${segment.tone || tone}`}
              style={segment.color ? { "--ds-circular-color": segment.color } : undefined}
              strokeDasharray={`${percentage} ${PROGRESS_PATH_LENGTH}`}
              transform={`rotate(${offset * 360 / PROGRESS_PATH_LENGTH} 50 50)`}
            >
              {segment.label ? <title>{segment.label}</title> : null}
            </circle>
          );
        }) : null}
      </svg>
      {/* shortcut: slices below 2% use the legend; add callouts if they need in-chart labels. */}
      {showSegmentPercentages && segmentRanges.filter(({ percentage }) => percentage >= 2).map(({ segment, percentage, offset }, index) => {
        const angle = (offset + percentage / 2) / PROGRESS_PATH_LENGTH * 360 - 90;
        const radians = angle / 180 * Math.PI;
        const x = 50 + RADIUS * Math.cos(radians);
        const y = 50 + RADIUS * Math.sin(radians);
        const rotation = percentage < 5 ? angle - (angle > 90 ? 180 : 0) : 0;
        return (
          <span key={segment.key ?? index} className="ds-circular-progress__percentage" aria-hidden="true"
            style={{ left: `${x}%`, top: `${y}%`, transform: `translate(-50%, -50%) rotate(${rotation}deg)` }}>
            {percentage.toFixed(1)}%
          </span>
        );
      })}
      <span className="ds-circular-progress__content">
        <strong>{visibleValue}</strong>
        {label ? <small>{label}</small> : null}
      </span>
    </div>
  );
}
